import { Injectable, Injector } from '@angular/core';
import { AuthConfig, AuthService as CapturumAuthService } from '@capturum/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { PageRoute } from '@core/enums/general/routes.enum';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { ResetUserState, SetUser, StopImpersonateUser } from '@core/state/user/user.actions';
import { tap } from 'rxjs/operators';
import { AuthProvider } from '@capturum/auth/lib/auth.provider';
import { UserModel } from '@features/user/models/user.model';
import { UrlParams } from '@core/enums/url-params.enum';
import { LocalParamsService } from '@core/services/local-params.service';
import { IntercomService } from './intercom.service';
import { RoleService } from '@features/role/services/role.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService extends CapturumAuthService implements AuthProvider {
    private readonly tempTokenKey = 'tempToken';
    private readonly twoFAReasonKey = 'twoFAReason';
    private readonly redirectAfterLoginKey = 'redirectAfterLogin';

    constructor(
        config: AuthConfig,
        private httpClient: HttpClient,
        private router: Router,
        private store: Store,
        private paramsService: LocalParamsService,
        private intercomService: IntercomService,
        private injector: Injector,
    ) {
        super(config, httpClient);
    }

    // Lazy injection to break circular dependency: AuthService → RoleService → UserService → AuthService
    private get roleService(): RoleService {
        return this.injector.get(RoleService);
    }

    public handleSuccessfulLogin(response: any): void {
        this.store.dispatch(new StopImpersonateUser());

        const searchParams = new URLSearchParams(window.location.search);
        const redirectUrl = searchParams.get(UrlParams.RedirectUrl) || '/';
        const roleKey = searchParams.get(UrlParams.RedirectRoleKey) || undefined;
        const endpoint = searchParams.get(UrlParams.Endpoint);
        const pageTitle = searchParams.get(UrlParams.PageTitle);

        localStorage.setItem(this.redirectAfterLoginKey, JSON.stringify({
            roleKey,
            route: redirectUrl,
        }));

        if (response.shouldSetup2FA) {
            localStorage.setItem(this.tempTokenKey, response.token);
            localStorage.setItem(this.twoFAReasonKey, response.reason);
            this.router.navigate([PageRoute.twoFactorActivate]);

            return;
        }

        if (response.method) {
            this.router.navigate([PageRoute.twoFactorLogin], {
                queryParams: {
                    method: response.method,
                    userId: response.user_id,
                },
            });
        } else {
            localStorage.removeItem(this.tempTokenKey);
            localStorage.removeItem(this.twoFAReasonKey);
            this.store.dispatch(new SetUser(this.getUser() as UserModel));
            this.intercomService.initializeIntercom();

            const queryParams: Record<string, string> = {};
            if (endpoint) queryParams[UrlParams.Endpoint] = endpoint;
            if (pageTitle) queryParams[UrlParams.PageTitle] = pageTitle;
            const navExtras = Object.keys(queryParams).length ? { queryParams } : undefined;

            if (roleKey) {
                localStorage.removeItem(this.redirectAfterLoginKey);
                this.roleService.setRoleByRoleKey(roleKey).subscribe({
                    next: () => this.router.navigate([redirectUrl], navExtras),
                    error: () => this.router.navigate([redirectUrl], navExtras),
                });
                return;
            }

            this.router.navigate([redirectUrl], navExtras);
        }
    }

    public activateUser(token: string, userId: string, postData: { password: string, password_confirmation: string }): Observable<void> {
        return this.httpClient.post<void>(`${environment.baseUrl}/auth/user/${userId}/activate`, postData, { params: { token } });
    }

    public logout(): Observable<boolean> {
        try {
            return super.logout().pipe(tap(() => {
                this.store.dispatch(new ResetUserState());
            }));
        } catch (e) {
            return null;
        }
    }

    public getHeaders(): HttpHeaders {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json',
        });

        const token = localStorage.getItem('token');

        if (token) {
            headers = headers.set('Authorization', 'Bearer ' + this.getToken());
        }

        return headers;
    }

    public getToken(): string {
        return localStorage.getItem('token');
    }
}
